import { useEffect, useMemo, useRef, useState } from 'react';
import { useAreas } from './hooks';
import type { Area } from '@matix/types';
import { useLang } from '../../lib/language';

/**
 * AreaPicker — searchable combobox over GL_Area for the address form.
 *
 * Spec section 3.7 / Issue 2 follow-up. Lazy-loads the full area list on
 * first mount (cached 1 hour via React Query), sorts alphabetically by
 * name (server already does this), and filters client-side by both name
 * and name_ar so the cashier can type either script.
 *
 * Display name uses `name_ar` when the document is in RTL/ar mode, else
 * `name`. Each option also shows the other-script name in muted text
 * underneath so cashiers can confirm at a glance.
 */
export function AreaPicker(props: {
  value: number | null;
  onChange: (id: number | null) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const { t } = useLang();
  const { data, isLoading, isError } = useAreas();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Close on outside-click so the popover doesn't trap focus.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setFilter('');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const areas: Area[] = data?.data ?? [];
  const selected = useMemo(
    () => areas.find((a) => a.id === props.value) ?? null,
    [areas, props.value],
  );

  const isArabic = isRtlOrArabic();

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return areas;
    return areas.filter((a) =>
      ((a.name ?? '') + ' ' + (a.name_ar ?? '')).toLowerCase().includes(needle),
    );
  }, [areas, filter]);

  function displayPrimary(a: Area | null): string {
    if (!a) return props.placeholder ?? t({ en: 'Select area', ar: 'اختر المنطقة' });
    const primary = isArabic ? a.name_ar : a.name;
    return (primary ?? a.name ?? a.name_ar ?? `#${a.id}`).trim();
  }

  function displaySecondary(a: Area): string | null {
    const other = (isArabic ? a.name : a.name_ar)?.trim();
    if (!other) return null;
    const primary = displayPrimary(a);
    return other === primary ? null : other;
  }

  return (
    <div className={`area-picker ${open ? 'open' : ''}`} ref={wrapperRef}>
      <button
        type="button"
        className="area-picker-trigger"
        disabled={props.disabled || isLoading}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={selected ? 'has-value' : 'placeholder'}>
          {isLoading ? t({ en: 'Loading areas…', ar: 'جارٍ تحميل المناطق…' }) : displayPrimary(selected)}
        </span>
        <span className="caret" aria-hidden>▾</span>
      </button>
      {selected && !props.disabled && (
        <button
          type="button"
          className="area-picker-clear"
          aria-label={t({ en: 'Clear area', ar: 'مسح المنطقة' })}
          onClick={(e) => {
            e.stopPropagation();
            props.onChange(null);
          }}
        >
          ×
        </button>
      )}
      {open && !isLoading && !isError && (
        <div className="area-picker-menu" role="listbox">
          <input
            className="area-picker-search"
            placeholder={t({ en: 'Search by name…', ar: 'ابحث بالاسم…' })}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            autoFocus
          />
          <ul className="area-picker-list">
            {filtered.slice(0, 200).map((a) => {
              const secondary = displaySecondary(a);
              return (
                <li
                  key={a.id}
                  role="option"
                  aria-selected={a.id === props.value}
                  className={`area-picker-option ${a.id === props.value ? 'selected' : ''}`}
                  onClick={() => {
                    props.onChange(a.id);
                    setOpen(false);
                    setFilter('');
                  }}
                >
                  <div className="area-name">{displayPrimary(a)}</div>
                  {secondary && <div className="area-name-alt">{secondary}</div>}
                </li>
              );
            })}
            {filtered.length === 0 && (
              <li className="area-picker-empty">{t({ en: 'No areas match', ar: 'لا توجد مناطق مطابقة' })}</li>
            )}
            {filtered.length > 200 && (
              <li className="area-picker-hint">
                {t({ en: `${filtered.length - 200} more — refine your search`, ar: `${filtered.length - 200} أخرى — نقّح بحثك` })}
              </li>
            )}
          </ul>
        </div>
      )}
      {isError && (
        <div className="area-picker-error">{t({ en: 'Couldn’t load areas.', ar: 'تعذّر تحميل المناطق.' })}</div>
      )}
    </div>
  );
}

function isRtlOrArabic(): boolean {
  if (typeof document === 'undefined') return false;
  const root = document.documentElement;
  return root.dir === 'rtl' || root.lang === 'ar' || root.lang?.startsWith('ar-');
}
